// PayU Payment Gateway Utility Functions

/**
 * Generate a unique transaction ID
 */
export const generateTransactionId = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000000);
  return `TXN${timestamp}${random}`;
};

/**
 * Calculate hash for PayU payment
 * Formula: sha512(key|txnid|amount|productinfo|firstname|email|udf1|udf2|udf3|udf4|udf5||||||salt)
 */
export const generatePayUHash = async (params, salt) => {
  const { 
    key, 
    txnid, 
    amount, 
    productinfo, 
    firstname, 
    email,
    udf1 = '',
    udf2 = '',
    udf3 = '',
    udf4 = '',
    udf5 = ''
  } = params;
  
  // Check if crypto.subtle is available
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error('Web Crypto API not available. Please use HTTPS or a modern browser.');
  }
  
  // Hash string format for PayU
  // Format: key|txnid|amount|productinfo|firstname|email|udf1|udf2|udf3|udf4|udf5||||||salt
  const hashString = `${key}|${txnid}|${amount}|${productinfo}|${firstname}|${email}|${udf1}|${udf2}|${udf3}|${udf4}|${udf5}||||||${salt}`;
  
  // Generate SHA-512 hash
  const msgBuffer = new TextEncoder().encode(hashString);
  const hashBuffer = await crypto.subtle.digest('SHA-512', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
};

/**
 * Initialize PayU payment form
 */
export const initiatePayUPayment = async (paymentData, config) => {
  const {
    amount,
    productInfo,
    firstName,
    email,
    phone,
    address1 = '',
    city = '',
    state = '',
    country = 'India',
    zipcode = '',
    udf1 = '',
    udf2 = '',
    udf3 = '',
    udf4 = '',
    udf5 = ''
  } = paymentData;

  const {
    merchantKey,
    merchantSalt,
    paymentUrl,
    successUrl,
    failureUrl,
    cancelUrl
  } = config;

  // Generate transaction ID
  const txnid = generateTransactionId();

  // Generate hash
  const hash = await generatePayUHash({
    key: merchantKey,
    txnid,
    amount,
    productinfo: productInfo,
    firstname: firstName,
    email,
    udf1,
    udf2,
    udf3,
    udf4,
    udf5
  }, merchantSalt);

  // Prepare PayU form data
  const formData = {
    key: merchantKey,
    txnid,
    amount,
    productinfo: productInfo,
    firstname: firstName,
    email,
    phone,
    surl: successUrl,
    furl: failureUrl,
    curl: cancelUrl,
    hash,
    address1,
    city,
    state,
    country,
    zipcode,
    udf1,
    udf2,
    udf3,
    udf4,
    udf5
  };

  // Create and submit form
  submitPayUForm(paymentUrl, formData);
};

/**
 * Create and submit PayU payment form
 */
const submitPayUForm = (paymentUrl, formData) => {
  // Create a form element
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = paymentUrl;
  form.style.display = 'none';

  // Add all form fields
  Object.keys(formData).forEach(key => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = formData[key];
    form.appendChild(input);
  });

  // Append form to body and submit
  document.body.appendChild(form);
  form.submit();
};

/**
 * Verify PayU payment response hash
 */
export const verifyPayUResponse = async (response, merchantSalt, merchantKey) => {
  const {
    status,
    txnid,
    amount,
    productinfo,
    firstname,
    email,
    udf1 = '',
    udf2 = '',
    udf3 = '',
    udf4 = '',
    udf5 = '',
    hash: receivedHash
  } = response;

  // Check if crypto.subtle is available
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error('Web Crypto API not available. Please use HTTPS or a modern browser.');
  }

  // For response verification, the hash string is in reverse order
  // Format: salt|status|udf5|udf4|udf3|udf2|udf1||||||email|firstname|productinfo|amount|txnid|key
  const hashString = `${merchantSalt}|${status}|${udf5}|${udf4}|${udf3}|${udf2}|${udf1}||||||${email}|${firstname}|${productinfo}|${amount}|${txnid}|${merchantKey}`;
  
  const msgBuffer = new TextEncoder().encode(hashString);
  const hashBuffer = await crypto.subtle.digest('SHA-512', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const calculatedHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return calculatedHash === receivedHash;
};

/**
 * Parse PayU response URL parameters
 */
export const parsePayUResponse = (queryString) => {
  // If full URL is provided, extract query string
  if (queryString.includes('?')) {
    queryString = queryString.split('?')[1];
  }
  
  const params = new URLSearchParams(queryString);
  const response = {};
  
  for (const [key, value] of params.entries()) {
    response[key] = value;
  }
  
  return response;
};

const payuUtils = {
  generateTransactionId,
  generatePayUHash,
  initiatePayUPayment,
  verifyPayUResponse,
  parsePayUResponse
};

export default payuUtils;
